// Render ```svg fences into real figures.
//
// Why this exists: mermaid was the chat's ONLY visual renderer, and mermaid is a
// GRAPH language — nodes and edges, no geometry primitives. So "explain a
// right-angled triangle with a diagram" could only come back as boxes and arrows
// (the reported bug), because there was no lane for a shape. This adds it.
//
// It's also how the rest of the industry draws geometry: Claude emits an SVG
// artifact, ChatGPT/Gemini run matplotlib in a sandbox. SVG is the cheaper half of
// that pair for anything hand-drawable — no code round-trip, crisp at any zoom,
// and it inherits the chat's own colours. The matplotlib path (agentic lane) stays
// for figures that must be COMPUTED: function curves, fitted plots.
//
// SECURITY: the SVG text comes from a model, so it is untrusted input that we are
// about to put into the DOM. SVG is a full XML document format — it can carry
// <script>, event handlers, <foreignObject> with arbitrary HTML, and external
// fetches — so this is a genuine XSS surface, not a formality. We sanitize with
// DOMPurify (already in the tree; mermaid depends on it, and mermaid's own
// securityLevel:'strict' uses it the same way) rather than hand-rolling a regex
// filter, because a hand-rolled allowlist is exactly the thing that gets bypassed.
// DOMPurify is loaded DYNAMICALLY so a chat with no figure never pays for it.

let purifyPromise: Promise<typeof import('dompurify').default> | null = null;

/** Load DOMPurify once, on first figure. */
function loadPurify() {
  if (!purifyPromise) purifyPromise = import('dompurify').then((m) => m.default);
  return purifyPromise;
}

/**
 * Sanitize one model-authored SVG document to something safe to inject.
 *
 * - `USE_PROFILES: { svg, svgFilters }` restricts the allowed elements and
 *   attributes to SVG's own, so no HTML sneaks in.
 * - `<foreignObject>` is forbidden explicitly: it is the documented hole in an
 *   SVG-only profile — it re-opens arbitrary HTML inside the drawing.
 * - `<script>`, `<use>` and every `on*` handler are forbidden: script execution
 *   and same-document reference tricks.
 * - `<image>`, `href`/`xlink:href` are forbidden so a figure cannot beacon out to
 *   a third party (a privacy leak as much as a security one) or load a remote
 *   payload. A geometric figure never needs them.
 *
 * Returns '' when nothing safe survives — the caller then leaves the source
 * visible, which is honest and readable, rather than showing an empty frame.
 */
function sanitizeSvg(purify: typeof import('dompurify').default, src: string): string {
  const clean = purify.sanitize(src, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ['script', 'foreignObject', 'image', 'use', 'style'],
    FORBID_ATTR: ['href', 'xlink:href', 'style', 'onload', 'onclick', 'onmouseover', 'onerror'],
  });
  // sanitize() can return a non-empty string that no longer contains a drawing
  // (e.g. the model wrote HTML in an svg fence). Only accept a real <svg> root.
  return /<svg[\s>]/i.test(clean) ? clean : '';
}

/**
 * A model-authored figure has no reason to be enormous, and an unbounded one is a
 * cheap way to hang the main thread. 64KB is ~20x a detailed hand-drawn figure.
 */
const MAX_SVG_BYTES = 64 * 1024;

/** Read `viewBox` tolerantly. Inline SVG in an HTML document keeps its camelCase
 *  attribute, but a model may well write `viewbox`, and on a foreign (SVG)
 *  element getAttribute IS case-sensitive — so check both rather than trust one. */
function readViewBox(svg: SVGElement): string | null {
  return svg.getAttribute('viewBox') ?? svg.getAttribute('viewbox');
}

/**
 * Make one figure scale to the chat column instead of its authored pixel size.
 *
 * The subtlety that caused a real bug: `width`/`height` may only be dropped once a
 * `viewBox` exists to replace them. A viewBox is what gives an SVG its coordinate
 * system and intrinsic aspect ratio; width/height alone give it a size. Remove the
 * size while there is no viewBox and the element has NEITHER — the browser falls
 * back to the CSS default 300x150, and because SVG defaults to `overflow: hidden`,
 * everything outside that box is CLIPPED rather than scaled. Observed exactly
 * that: a flowchart rendered as its own top-left corner, nodes sliced mid-word.
 *
 * So: synthesise the viewBox from the authored size first, and only then drop it.
 * A figure with neither attribute is left untouched — whatever the browser infers
 * is better than us inventing a coordinate system for it.
 */
export function fitToColumn(svg: SVGElement): void {
  if (!readViewBox(svg)) {
    // Authored sizes may carry units ("640px", "12cm"); parseFloat takes the
    // leading number, which is what a user-units viewBox wants.
    const w = Number.parseFloat(svg.getAttribute('width') ?? '');
    const h = Number.parseFloat(svg.getAttribute('height') ?? '');
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    }
  }
  // Still nothing to go on — the model gave no viewBox AND no size. Left alone
  // this clips exactly like the bug above, so MEASURE the drawing instead: getBBox
  // returns the real extent of the painted content, which is precisely the viewBox
  // we would have wanted. Only possible because `svg` is already in the live
  // document by the time we get here (innerHTML was assigned above) — getBBox has
  // no meaning on a detached node.
  //
  // Best-effort and unverifiable in a headless DOM (linkedom implements no layout),
  // hence the guards: a throw, or a zero-area box from content that paints nothing,
  // leaves the element untouched rather than stamping a bogus coordinate system.
  if (!readViewBox(svg) && typeof (svg as SVGGraphicsElement).getBBox === 'function') {
    try {
      const b = (svg as SVGGraphicsElement).getBBox();
      if (b.width > 0 && b.height > 0) {
        // Round outward with a small pad so a stroke centred on the edge is not
        // shaved off by half its width.
        const pad = 2;
        svg.setAttribute(
          'viewBox',
          `${Math.floor(b.x - pad)} ${Math.floor(b.y - pad)} ${Math.ceil(b.width + pad * 2)} ${Math.ceil(b.height + pad * 2)}`,
        );
      }
    } catch {
      /* no layout available — fall through and leave the element as authored */
    }
  }
  if (readViewBox(svg)) {
    // Now safe: the viewBox carries the geometry, so CSS can drive the size.
    svg.removeAttribute('width');
    svg.removeAttribute('height');
  }
}

/**
 * Find un-rendered .svg-block elements under `root` and draw them.
 *
 * Mirrors renderMermaidIn: safe to call on every render/stream tick (each block is
 * marked done, so work happens once), and a block that isn't a complete, valid
 * <svg> yet is LEFT as its <pre> fallback rather than replaced with an error —
 * while a reply is streaming the fence is legitimately half-written, and flashing
 * a failure at the user for one tick is worse than showing the source for one tick.
 */
export async function renderSvgIn(root: HTMLElement | null): Promise<void> {
  if (!root) return;
  const blocks = Array.from(
    root.querySelectorAll<HTMLElement>('.svg-block[data-svg]:not([data-svg-done])'),
  );
  if (blocks.length === 0) return;

  let purify: Awaited<ReturnType<typeof loadPurify>>;
  try {
    purify = await loadPurify();
  } catch {
    return; // offline / chunk failed → the <pre> source stays, which is readable
  }

  for (const el of blocks) {
    const src = el.getAttribute('data-svg') ?? '';
    if (!src.trim() || src.length > MAX_SVG_BYTES) continue;
    // Still streaming: no closing tag yet. Try again on the next tick.
    if (!/<\/svg\s*>/i.test(src)) continue;
    try {
      const clean = sanitizeSvg(purify, src);
      if (!clean) continue;
      el.innerHTML = clean;
      const svg = el.querySelector('svg');
      if (svg) fitToColumn(svg);
      el.setAttribute('data-svg-done', '1');
    } catch {
      // Malformed or half-streamed — leave the source visible and retry.
    }
  }
}
